/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, map, Observable } from 'rxjs';
import { BaseService } from './base.service';
import { Response } from '../interfaces/api/build';
import { ModalController, NavController } from '@ionic/angular';
import { ControllerService } from './controller.service';
import { ModalCompleteAccountComponent } from '../components/modal-complete-account/modal-complete-account.component';
import { FirebaseChat } from '../models/firebase-chat.model';
import { FbMeUser, FbYouUser } from '../models/firebase-user.model';
import { ChatActionEnum } from '../models/chat-action.enum';
import { AngularFirestore, AngularFirestoreCollection, DocumentReference } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';

@Injectable({
  providedIn: 'root',
})
export class ChatroomService extends BaseService {
  private chatroomUrl = `${this.apiUrl}/chatroom`;

  private chatroomChatsCollection: AngularFirestoreCollection<FirebaseChat>;
  private chatroomChats: Observable<any>;
  private chatroom_id: string;

  me = new BehaviorSubject<FbMeUser>(null);
  me$ = this.me.asObservable();

  you = new BehaviorSubject<FbYouUser>(null);
  you$ = this.you.asObservable();

  repliedMessage = new BehaviorSubject<FirebaseChat>(null);
  repliedMessage$ = this.repliedMessage.asObservable();

  image_upload = new BehaviorSubject<string>(null);
  image_upload$ = this.image_upload.asObservable();

  image_base64 = new BehaviorSubject<string>(null);
  image_base64$ = this.image_base64.asObservable();

  constructor(
    private afs: AngularFirestore,
    private http: HttpClient,
    private navController: NavController,
    private controllerService: ControllerService,
    private modalController: ModalController,
  ) {
    super();
  }

  getImageBase64() {
    return this.image_base64.getValue();
  }

  setImageBase64(img: string) {
    this.image_base64.next(img);
  }

  getImageUpload() {
    return this.image_upload.getValue();
  }

  setImageUpload(img: string) {
    this.image_upload.next(img);
  }

  getRepliedMessage() {
    return this.repliedMessage.getValue();
  }

  setRepliedMessage(msg: FirebaseChat) {
    this.repliedMessage.next(msg);
  }

  getMe() {
    return this.me.getValue();
  }

  setMe(user: FbMeUser) {
    this.me.next(user);
  }

  getYou() {
    return this.you.getValue();
  }

  setYou(user: FbYouUser) {
    this.you.next(user);
  }

  updateLastActive(id: number): Observable<Response> {
    return this.http.post<Response>(`${this.chatroomUrl}/${id}/setting/lastactive`, {});
  }

  sendNotificationChat(chatroom_id: number, message: string): Observable<Response> {
    return this.http.post<Response>(`${this.chatroomUrl}/${chatroom_id}/sendnotification?no_loading=true`, { message });
  }

  uploadImageChatroom(payload: { image: string }) {
    return this.http.post<{
      message: string;
      data: string;
    }>(`${this.chatroomUrl}/image-upload`, payload);
  }

  async openModalCompleteAccount() {
    const modal = await this.modalController.create({
      component: ModalCompleteAccountComponent,
      cssClass: 'modal-bottom',
    });

    modal.present();

    // const { data, role } = await modal.onDidDismiss();
  }

  validateUser(userName: string, userEmail: string) {
    if (!userName || !userEmail) {
      return false;
    } else {
      return true;
    }
  }

  goToChatroomPage(userName: string, userEmail: string) {
    if (!this.validateUser(userName, userEmail)) {
      this.openModalCompleteAccount();
      return;
    }

    this.navController.navigateForward(`/chatroom`);
  }

  isMe(chat: FirebaseChat) {
    const me = this.getMe();

    return chat?.sender.id === me?.user.id && chat?.sender.role === me?.role;
  }

  async sendMessage(message: string, chatroomId: string, callback?: () => void) {
    let image_upload_url = null;
    const image_upload_base64 = this.getImageBase64();
    const me = this.getMe();
    const repliedMessage = this.getRepliedMessage();

    if (image_upload_base64) {
      try {
        const res = await firstValueFrom(this.uploadImageChatroom({ image: image_upload_base64 }));
        image_upload_url = res.data;
        console.log(image_upload_url);
      } catch (error) {
        this.controllerService.presentAlert({
          header: 'Error',
          message: 'Failed to upload image',
          buttons: ['OK'],
        });
      }
    }
    // console.log(image_upload_url);
    const hasNoMessage = message?.trim() === '';
    if (hasNoMessage && !image_upload_url) {
      return;
    }
    if (!!image_upload_url && hasNoMessage) {
      this.controllerService.presentToast({
        message: 'Mohon berikan deskripsi gambar terlebih dahulu',
        position: 'top',
        color: 'warning',
        duration: 2000,
      });
      return;
    }
    const chat: FirebaseChat = {
      sender: {
        id: me.user.id,
        role: me.role,
      },
      send_date: Timestamp.fromDate(new Date()),
      action: ChatActionEnum.Message,
      message,
      image_url: image_upload_url,
      reply_message: repliedMessage,
    };
    this.setChatroomChatCollection(chatroomId);
    this.addChatroomChat(chat, me, (res: DocumentReference<FirebaseChat>) => {
      const notifReq = this.sendNotificationChat(Number(chatroomId), this.convertChatToMessage(chat, me.user.name));
      const subs = forkJoin([notifReq]).subscribe({
        next: subRes => {
          subs.unsubscribe();
        },
      });
      // console.log(res.id);
    }).then(() => {
      callback();
    });
  }

  async setChatroomChatCollection(chatroomId: string): Promise<void> {
    this.chatroom_id = chatroomId;

    try {
      this.chatroomChatsCollection = this.afs
        .collection('chatroom')
        .doc(chatroomId)
        .collection<FirebaseChat>('chat', ref => ref.orderBy('send_date', 'asc'));
      this.chatroomChats = this.chatroomChatsCollection.valueChanges({ idField: 'id' }).pipe(
        map(chatroomChats =>
          chatroomChats.map(chatroomChat => {
            let reply = null;
            if (chatroomChat.reply_message) {
              reply = {
                ...chatroomChat.reply_message,
                send_date: chatroomChat.reply_message.send_date ? chatroomChat.reply_message.send_date.toDate() : null,
              };
            }
            return {
              ...chatroomChat,
              reply_message: reply,
              send_date: chatroomChat.send_date ? chatroomChat.send_date.toDate() : null,
            };
          }),
        ),
      );

      return Promise.resolve();
    } catch (error) {
      console.log('set chatroom error');
      return Promise.reject();
    }
  }

  addChatroomChat(chat: FirebaseChat, me: FbMeUser, callback?: (res: DocumentReference<FirebaseChat>) => void) {
    try {
      this.chatroomChatsCollection.add(chat).then(res => {
        callback(res);
      });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  getChatroomChats(): Observable<FirebaseChat[]> {
    return this.chatroomChats;
  }

  convertChatToMessage = (chat: FirebaseChat, sender_name: string): string => {
    if (chat.action === ChatActionEnum.Message || chat.action === ChatActionEnum.Leave) {
      return chat.message;
    } else if (chat.action === ChatActionEnum.Image) {
      return sender_name + ' mengirim sebuah gambar';
    } else if (chat.action === ChatActionEnum.Appointment) {
      return `Dokter ${sender_name} membuat sebuah jadwal baru`;
    } else if (chat.action === ChatActionEnum.File) {
      return sender_name + ' mengirim sebuah dokumen';
    } else {
      return 'Ada pesan baru';
    }
  };
}
